import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  createBoardAiConversation,
  deleteBoardAiConversation,
  fetchBoardAiConversations,
  postBoardAiChat,
  postBoardAiChatStream,
} from '../api/boardAiApi';
import { buildClientContext } from '../utils/buildClientContext';
import {
  collectBoardSelectionItems,
  selectionItemsToContext,
  buildPinLabelById,
} from '../utils/boardSelectionContext';
import { expandSelectionItemsForAiContext } from '../utils/expandSelectionForAiContext';
import { findCachedAskConversation } from '../utils/findCachedAskConversation';
import { INTENT_EVENT_TYPES } from '../utils/boardRecentIntent';
import { recordBoardIntent } from '../utils/recordBoardIntent';

function attachAiInput(conversation, aiInput) {
  if (!conversation || !aiInput) return conversation;
  const messages = Array.isArray(conversation.messages) ? conversation.messages.map(item => ({ ...item })) : [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    if (messages[i]?.role === 'assistant') {
      messages[i] = { ...messages[i], aiInput };
      break;
    }
  }
  return { ...conversation, messages };
}

function lastUserQuestion(conversation) {
  const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const text = String(messages[i]?.content || '').trim();
    if (messages[i]?.role === 'user' && text) return text;
  }
  return '';
}

function conversationIsPinned(editor, conversationId) {
  const needle = String(conversationId || '').trim();
  if (!editor?.getCurrentPageShapes || !needle) return false;
  for (const shape of editor.getCurrentPageShapes() || []) {
    if (shape?.type !== 'conversation-pin') continue;
    const id = String(shape.meta?.conversationId || shape.props?.conversationId || '');
    if (id === needle) return true;
  }
  return false;
}

function createLocalConversationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `conv_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function isEmptyConversation(conversation) {
  return Boolean(conversation) && !(conversation.messages?.length > 0);
}

function findEmptyConversation(conversations = [], { allowDraft = true } = {}) {
  return (
    conversations.find(
      item => isEmptyConversation(item) && (allowDraft || !item._localDraft)
    ) || null
  );
}

/** Prefer server rows; keep local-only empties that a stale fetch may not include yet. */
function mergeConversationLists(serverList = [], localList = []) {
  const byId = new Map();
  for (const item of serverList) {
    if (item?.id) byId.set(item.id, item);
  }
  for (const item of localList) {
    if (!item?.id || byId.has(item.id)) continue;
    if (isEmptyConversation(item)) byId.set(item.id, item);
  }
  const merged = Array.from(byId.values()).sort((a, b) => {
    const aTime = new Date(a.updatedAt || 0).getTime();
    const bTime = new Date(b.updatedAt || 0).getTime();
    return bTime - aTime;
  });

  let emptyKept = false;
  return merged.filter(item => {
    if (!isEmptyConversation(item)) return true;
    if (emptyKept) return false;
    emptyKept = true;
    return true;
  });
}

export function useBoardConversation({
  hashtag,
  editorRef,
  board,
  activities,
  updatePlacementsLocal,
  savePlacements,
  saveFrames,
  saveSnapshot,
  deleteActivities,
  enabled = true,
  chatModel = 'better',
}) {
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [input, setInput] = useState('');
  const [removedContextKeys, setRemovedContextKeys] = useState(() => new Set());
  const [selectionItems, setSelectionItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [creatingConversation, setCreatingConversation] = useState(false);
  const [error, setError] = useState(null);
  const [focusMessageAt, setFocusMessageAt] = useState(null);
  const [thinkingPhase, setThinkingPhase] = useState('');
  const [lastJobMetrics, setLastJobMetrics] = useState(null);
  const bootstrappedRef = useRef(false);
  const conversationsRef = useRef([]);
  const activeConversationIdRef = useRef(null);
  const createInFlightRef = useRef(null);
  const loadGenerationRef = useRef(0);

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const activeConversation = useMemo(
    () => conversations.find(item => item.id === activeConversationId) || null,
    [conversations, activeConversationId]
  );

  const messages = activeConversation?.messages || [];

  const pinLabelById = useMemo(() => buildPinLabelById(activities), [activities]);

  const refreshSelection = useCallback(() => {
    const editor = editorRef.current;
    const items = collectBoardSelectionItems(editor, { activities });
    setSelectionItems(expandSelectionItemsForAiContext(editor, items, { activities }));
  }, [editorRef, activities]);

  const composerContextItems = useMemo(
    () => selectionItems.filter(item => !removedContextKeys.has(item.key)),
    [selectionItems, removedContextKeys]
  );

  // When chat is open, mirror canvas selection into composer context chips.
  // Retry attach until editorRef is set — BoardChatLauncher mounts before Tldraw onMount.
  useEffect(() => {
    if (!enabled || !open) {
      setSelectionItems([]);
      return undefined;
    }

    let cancelled = false;
    let unlisten = null;
    let rafId = null;

    const attach = () => {
      if (cancelled) return;
      const editor = editorRef.current;
      if (!editor?.store?.listen) {
        rafId = requestAnimationFrame(attach);
        return;
      }
      refreshSelection();
      unlisten = editor.store.listen(() => {
        refreshSelection();
      }, { scope: 'session' });
    };

    attach();

    return () => {
      cancelled = true;
      if (rafId != null) cancelAnimationFrame(rafId);
      unlisten?.();
    };
  }, [enabled, open, editorRef, refreshSelection]);

  useEffect(() => {
    const currentKeys = new Set(selectionItems.map(item => item.key));
    setRemovedContextKeys(prev => {
      const next = new Set([...prev].filter(key => currentKeys.has(key)));
      return next.size === prev.size ? prev : next;
    });
  }, [selectionItems]);

  const loadConversations = useCallback(async () => {
    if (!hashtag) return;
    const generation = ++loadGenerationRef.current;
    const result = await fetchBoardAiConversations(hashtag);
    if (generation !== loadGenerationRef.current) return;
    const list = result.conversations || [];
    setConversations(prev => mergeConversationLists(list, prev));
    setActiveConversationId(current => {
      if (current && list.some(item => item.id === current)) return current;
      // Keep a just-created / local empty selection that a stale fetch may omit.
      if (current && conversationsRef.current.some(item => item.id === current)) return current;
      return list[0]?.id || current || null;
    });
  }, [hashtag]);

  const createEmptyConversation = useCallback(async () => {
    if (createInFlightRef.current) return createInFlightRef.current;

    const empty = findEmptyConversation(conversationsRef.current, { allowDraft: false });
    if (empty?.id) {
      setActiveConversationId(empty.id);
      return empty.id;
    }

    const pending = (async () => {
      setCreatingConversation(true);
      try {
        const result = await createBoardAiConversation(hashtag);
        const conversation = result.conversation;
        if (!conversation?.id) {
          throw new Error('Failed to create conversation');
        }
        setConversations(prev => {
          const withoutServerDup = prev.filter(item => item.id !== conversation.id && !item._localDraft);
          const existingEmpty = findEmptyConversation(withoutServerDup, { allowDraft: false });
          const nextRest = existingEmpty
            ? withoutServerDup.filter(item => item.id !== existingEmpty.id)
            : withoutServerDup;
          return [conversation, ...nextRest];
        });
        setActiveConversationId(conversation.id);
        return conversation.id;
      } finally {
        setCreatingConversation(false);
        createInFlightRef.current = null;
      }
    })();

    createInFlightRef.current = pending;
    return pending;
  }, [hashtag]);

  const ensureConversation = useCallback(async () => {
    const current = activeConversationIdRef.current;
    if (current) {
      const active = conversationsRef.current.find(item => item.id === current);
      if (active?._localDraft && createInFlightRef.current) {
        return createInFlightRef.current;
      }
      if (active && !active._localDraft) return current;
      if (active?._localDraft) {
        // Draft still resolving — wait for create if in flight, else create now.
        return createEmptyConversation();
      }
      return current;
    }
    return createEmptyConversation();
  }, [createEmptyConversation]);

  const selectEmptyComposer = useCallback(() => {
    setError(null);
    setRemovedContextKeys(new Set());
    setInput('');
    setFocusMessageAt(null);
  }, []);

  const startNewConversation = useCallback(async () => {
    selectEmptyComposer();

    if (createInFlightRef.current) {
      const id = await createInFlightRef.current;
      setActiveConversationId(id);
      return id;
    }

    const empty = findEmptyConversation(conversationsRef.current, { allowDraft: false });
    if (empty?.id) {
      setActiveConversationId(empty.id);
      return empty.id;
    }

    // Optimistic blank chat so the panel switches immediately.
    const localId = createLocalConversationId();
    const draft = {
      id: localId,
      title: 'New chat',
      mode: 'agent',
      messages: [],
      updatedAt: new Date().toISOString(),
      _localDraft: true,
    };
    setConversations(prev => {
      if (findEmptyConversation(prev, { allowDraft: false })) return prev;
      if (prev.some(item => item._localDraft)) return prev;
      return [draft, ...prev];
    });
    setActiveConversationId(localId);

    const pending = (async () => {
      setCreatingConversation(true);
      try {
        const result = await createBoardAiConversation(hashtag);
        const conversation = result.conversation;
        if (!conversation?.id) {
          throw new Error('Failed to create conversation');
        }
        setConversations(prev => {
          const withoutDraft = prev.filter(
            item => item.id !== localId && item.id !== conversation.id && !item._localDraft
          );
          const otherEmpty = findEmptyConversation(withoutDraft, { allowDraft: false });
          const next = otherEmpty
            ? withoutDraft.filter(item => item.id !== otherEmpty.id)
            : withoutDraft;
          return [conversation, ...next];
        });
        setActiveConversationId(current => (
          current === localId || !current ? conversation.id : current
        ));
        return conversation.id;
      } catch (err) {
        setConversations(prev => prev.filter(item => item.id !== localId));
        setActiveConversationId(current => {
          if (current !== localId) return current;
          return findEmptyConversation(conversationsRef.current, { allowDraft: false })?.id
            || conversationsRef.current[0]?.id
            || null;
        });
        setError(err.message || 'Failed to create conversation');
        throw err;
      } finally {
        setCreatingConversation(false);
        createInFlightRef.current = null;
      }
    })();

    createInFlightRef.current = pending;
    return pending;
  }, [hashtag, selectEmptyComposer]);

  const openNewChat = useCallback(async () => {
    setOpen(true);
    selectEmptyComposer();
    try {
      // Blank chat paints immediately; list fetch runs in the background and
      // surfaces a spinner only inside the conversation select.
      if (!bootstrappedRef.current && hashtag) {
        bootstrappedRef.current = true;
        setBootstrapping(true);
        loadConversations()
          .catch(err => {
            setError(err.message || 'Failed to load conversations');
          })
          .finally(() => setBootstrapping(false));
      }
      await startNewConversation();
    } catch {
      // Error already surfaced on the panel.
    }
  }, [hashtag, loadConversations, selectEmptyComposer, startNewConversation]);

  const removeConversation = useCallback(async conversationId => {
    if (!conversationId) return;
    const existing = (conversationsRef.current || []).find(item => item.id === conversationId);
    const question = lastUserQuestion(existing);
    const pinned = conversationIsPinned(editorRef.current, conversationId);
    await deleteBoardAiConversation(hashtag, conversationId);
    if (question && !pinned) {
      recordBoardIntent(hashtag, {
        type: INTENT_EVENT_TYPES.ASK_CLEARED,
        label: question,
      });
    }
    setConversations(prev => {
      const next = prev.filter(item => item.id !== conversationId);
      setActiveConversationId(current => (current === conversationId ? next[0]?.id || null : current));
      return next;
    });
  }, [hashtag, editorRef]);

  const removeComposerContextItem = useCallback(key => {
    setRemovedContextKeys(prev => new Set([...prev, key]));
  }, []);

  const sendMessage = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || loading || !hashtag) return;

    setLoading(true);
    setError(null);
    setThinkingPhase('preparing');

    try {
      const editor = editorRef.current;
      const { pinIds: contextPinIds, shapeIds: contextShapeIds } = selectionItemsToContext(
        composerContextItems
      );
      const askedIntent = recordBoardIntent(hashtag, {
        type: INTENT_EVENT_TYPES.ASKED,
        label: trimmed,
      });
      let clientContext = await buildClientContext(editor, {
        command: trimmed,
        board: { ...board, recentIntent: askedIntent || board?.recentIntent || '' },
      });
      clientContext = {
        ...clientContext,
        selection: {
          pinIds: contextPinIds,
          shapeIds: contextShapeIds,
        },
      };
      const conversationId = await ensureConversation();
      const hasComposerContext = contextPinIds.length > 0 || contextShapeIds.length > 0;

      const chatPayload = {
        conversationId,
        message: trimmed,
        contextPinIds,
        contextShapeIds,
        contextItems: composerContextItems.map(({ kind, type, id, title, label }) => ({
          kind: kind || type,
          type,
          id,
          title,
          label,
        })),
        includeSelection: hasComposerContext,
        chatModel,
      };

      let result;
      try {
        result = await postBoardAiChatStream(hashtag, chatPayload, clientContext, {
          onEvent: (eventName, data) => {
            if (eventName === 'thought' && data?.text) {
              setThinkingPhase('thinking');
            } else if (eventName === 'reply_delta' && data?.text != null) {
              setThinkingPhase('building');
            } else if (eventName === 'phase' && data?.phase) {
              setThinkingPhase(String(data.phase));
            }
          },
        });
      } catch (streamErr) {
        console.warn('[board-chat] stream failed, falling back', streamErr?.message);
        setThinkingPhase('thinking');
        result = await postBoardAiChat(hashtag, chatPayload, clientContext);
      }

      if (result.conversation) {
        const conversation = attachAiInput(result.conversation, result.aiInput);
        setConversations(prev => {
          const without = prev.filter(item => item.id !== conversation.id);
          return [conversation, ...without];
        });
        setActiveConversationId(conversation.id);
      }

      if (result.jobMetrics || result.aiInput) {
        setLastJobMetrics({
          ...(result.jobMetrics || {}),
          ...(result.aiInput ? { aiInput: result.aiInput } : {}),
        });
      }

      // Chat is reply-only — never auto-mutate the canvas from chat responses.
      setInput('');
      setRemovedContextKeys(new Set());
    } catch (err) {
      setError(err.message || 'Chat request failed');
    } finally {
      setLoading(false);
      setThinkingPhase('');
    }
  }, [
    input,
    loading,
    hashtag,
    editorRef,
    board,
    ensureConversation,
    composerContextItems,
    chatModel,
  ]);

  const startChatWithMessage = useCallback(async (message, options = {}) => {
    const trimmed = String(message || '').trim();
    if (!trimmed || loading || !hashtag) return;

    setOpen(true);
    setError(null);
    setInput('');
    setRemovedContextKeys(new Set());

    const reuseQuestion = String(options.reuseQuestion || '').trim();
    if (reuseQuestion) {
      try {
        let list = conversations;
        if (!bootstrappedRef.current) {
          const loaded = await fetchBoardAiConversations(hashtag);
          list = loaded.conversations || [];
          setConversations(list);
          bootstrappedRef.current = true;
        }
        const cached = findCachedAskConversation(list, reuseQuestion);
        if (cached?.id) {
          setActiveConversationId(cached.id);
          return;
        }
      } catch {
        // Fall through and run a fresh ask.
      }
    }

    setLoading(true);
    setThinkingPhase('preparing');

    try {
      let conversationId = activeConversationIdRef.current;
      if (options.newConversation !== false) {
        const empty = findEmptyConversation(conversationsRef.current, { allowDraft: false });
        if (empty?.id) {
          conversationId = empty.id;
          setActiveConversationId(empty.id);
        } else {
          conversationId = await createEmptyConversation();
        }
      } else if (!conversationId) {
        conversationId = await ensureConversation();
      }

      const editor = editorRef.current;
      const seededItems = Array.isArray(options.contextItems) && options.contextItems.length
        ? options.contextItems
        : composerContextItems;
      if (seededItems !== composerContextItems) {
        setSelectionItems(seededItems);
      }
      const { pinIds: contextPinIds, shapeIds: contextShapeIds } = selectionItemsToContext(
        seededItems
      );
      const askedIntent = recordBoardIntent(hashtag, {
        type: INTENT_EVENT_TYPES.ASKED,
        label: trimmed,
      });
      let clientContext = await buildClientContext(editor, {
        command: trimmed,
        board: { ...board, recentIntent: askedIntent || board?.recentIntent || '' },
      });
      clientContext = {
        ...clientContext,
        selection: {
          pinIds: contextPinIds,
          shapeIds: contextShapeIds,
        },
      };
      const hasComposerContext = contextPinIds.length > 0 || contextShapeIds.length > 0;

      const chatPayload = {
        conversationId,
        message: trimmed,
        contextPinIds,
        contextShapeIds,
        contextItems: seededItems.map(({ kind, type, id, title, label }) => ({
          kind: kind || type,
          type,
          id,
          title,
          label,
        })),
        includeSelection: hasComposerContext,
        chatModel,
      };

      let result;
      try {
        result = await postBoardAiChatStream(hashtag, chatPayload, clientContext, {
          onEvent: (eventName, data) => {
            if (eventName === 'thought' && data?.text) {
              setThinkingPhase('thinking');
            } else if (eventName === 'reply_delta' && data?.text != null) {
              setThinkingPhase('building');
            } else if (eventName === 'phase' && data?.phase) {
              setThinkingPhase(String(data.phase));
            }
          },
        });
      } catch (streamErr) {
        console.warn('[board-chat] stream failed, falling back', streamErr?.message);
        setThinkingPhase('thinking');
        result = await postBoardAiChat(hashtag, chatPayload, clientContext);
      }

      if (result.conversation) {
        const conversation = attachAiInput(result.conversation, result.aiInput);
        setConversations(prev => {
          const without = prev.filter(item => item.id !== conversation.id);
          return [conversation, ...without];
        });
        setActiveConversationId(conversation.id);
      }

      if (result.jobMetrics || result.aiInput) {
        setLastJobMetrics({
          ...(result.jobMetrics || {}),
          ...(result.aiInput ? { aiInput: result.aiInput } : {}),
        });
      }

      // Chat is reply-only — never auto-mutate the canvas from chat responses.
    } catch (err) {
      setError(err.message || 'Chat request failed');
    } finally {
      setLoading(false);
      setThinkingPhase('');
    }
  }, [
    loading,
    hashtag,
    conversations,
    ensureConversation,
    createEmptyConversation,
    editorRef,
    board,
    composerContextItems,
    chatModel,
  ]);

  const openConversation = useCallback(async (conversationId, options = {}) => {
    if (!conversationId) return;
    const id = String(conversationId);
    setOpen(true);
    setActiveConversationId(id);
    setFocusMessageAt(options.messageAt ? String(options.messageAt) : null);
    setError(null);
    setInput('');

    const ensureListed = async () => {
      const listed = conversationsRef.current.some(item => item.id === id);
      if (listed) return;
      try {
        await loadConversations();
      } catch (err) {
        setError(err.message || 'Failed to load conversations');
      }
    };

    if (!bootstrappedRef.current) {
      bootstrappedRef.current = true;
      setBootstrapping(true);
      try {
        await loadConversations();
      } catch (err) {
        setError(err.message || 'Failed to load conversations');
      } finally {
        setBootstrapping(false);
      }
      return;
    }

    await ensureListed();
  }, [loadConversations]);

  const clearFocusMessage = useCallback(() => {
    setFocusMessageAt(null);
  }, []);

  useEffect(() => {
    if (!enabled || !open || bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    setBootstrapping(true);
    loadConversations()
      .catch(err => setError(err.message || 'Failed to load conversations'))
      .finally(() => setBootstrapping(false));
  }, [enabled, open, loadConversations]);

  return {
    open,
    setOpen,
    conversations,
    activeConversationId,
    setActiveConversationId,
    activeConversation,
    messages,
    input,
    setInput,
    composerContextItems,
    removeComposerContextItem,
    loading,
    bootstrapping,
    creatingConversation,
    error,
    thinkingPhase,
    lastJobMetrics,
    sendMessage,
    startChatWithMessage,
    startNewConversation,
    openNewChat,
    removeConversation,
    openConversation,
    focusMessageAt,
    clearFocusMessage,
    pinLabelById,
  };
}

export { createLocalConversationId };
